<template>
    <DataTable
        stripedRows
        hover
        :value="computedRows"
        :dt="{
            bodyCell: {
                padding: '0.5rem 1rem'
            }
        }"
    >
        <Column
            field="label"
            :pt="{
                root: (node) => {
                    return {
                        className: node.parent.props?.rowData?.key === 'building_parts' ? 'hidden' : ''
                    }
                },
                bodyCell: (node) => {
                    return {
                        className: node.parent.props?.rowData?.key === 'building_parts' ? 'hidden' : 'w-1/3'
                    }
                }
            }"
            :ptOptions="{mergeProps: true}"
        >
            <template #body="slotProps">
                <span class="block">{{ slotProps.data.label }}</span>
            </template>
        </Column>
        <Column field="value" :pt="{
            bodyCell: (node) => {
                return {
                    className: node.parent.props?.rowData?.key === 'building_parts' ? 'w-full bg-white dark:bg-surface-900' : ''
                }
            }
        }" :colspan="2" :ptOptions="{mergeProps: true}">
            <template #body="slotProps">
                <a v-if="slotProps.data.key === 'ehr_kood'" :href="createEhrLink(slotProps.data)" target="_blank" class="text-blue-500">
                    <span class="block">{{ slotProps.data.value }}</span>
                </a>
                <div v-else-if="slotProps.data.key === 'building_parts'">
                    <div>
                        {{ slotProps.data.label }}
                    </div>
                    <div class="flex flex-col gap-1 mb-2">
                        <div
                            v-for="part in buildingParts"
                            :key="part.id"
                        >
                            <div class="p-2 border shadow-xs rounded-md border-gray-200 dark:border-gray-700 bg-surface-50 dark:bg-surface-900 hover:bg-surface-100 dark:hover:bg-surface-800 mt-2 flex justify-between items-center" @click="togglePart(part.id)"
                            :class="{
                                'bg-surface-100 dark:bg-surface-900': expandedParts.includes(part.id),
                                'cursor-pointer': part.value.length > 1,
                                'border-b-0 rounded-b-none': expandedParts.includes(part.id)
                            }">
                                <span class="font-medium">{{ part.key }}</span>
                                <div class="flex items-center gap-2">
                                    <span class="px-1 py-0.5 bg-primary-100 text-xs text-primary flex items-center rounded-md">
                                        {{ part.area }} m²
                                    </span>
                                    <span class="text-sm text-gray-500 dark:text-gray-400 flex items-center">
                                        <i class="pi pi-chevron-down" v-if="!expandedParts.includes(part.id)"></i>
                                        <i class="pi pi-chevron-up" v-else></i>
                                    </span>
                                </div>
                            </div>
                            <div class="rounded-md -mt-0.5 border border-t-0 border-gray-200 dark:border-gray-700" :class="{'hidden': !expandedParts.includes(part.id)}">
                                <div v-for="field in part.value" :key="field.key" class="even:bg-surface-50 odd:bg-white dark:bg-surface-900 py-2 border-t first:border-t-0 border-gray-200 dark:border-gray-700 px-4">
                                    <div class="flex justify-between items-center text-sm">
                                        <span class="w-1/2">{{ field.label }}</span>
                                        <div class="w-1/2">
                                            <span v-if="field.key === 'hooneosa_seisukord'">
                                                <RatingMultiSelect
                                                    v-model="store.selectedTransaction.building_parts[part.id][field.key]"
                                                    field="hooneosa_seisukord"
                                                    :options="[1, 2, 3, 4, 5]"
                                                    placeholder="Vali kuni 2 hinnangut"
                                                    :maxSelectedLabels="2"
                                                    :selectionLimit="2"
                                                    size="small"
                                                    class="w-full max-w-96"
                                                    @update:model-value="updateBuildingPart(part.id, field.key, $event)"
                                                    tooltip-position="left"
                                                />
                                            </span>
                                            <span v-else-if="field.key === 'rodu'">
                                              <div class="flex items-center gap-4 w-full max-w-96">
                                                <span v-if="part.balcony" class="w-12">
                                                  {{ part.balcony }}
                                                </span>
                                                <RatingMultiSelect
                                                    v-model="store.selectedTransaction.building_parts[part.id][field.key]"
                                                    :options="[0, 1, 2, 3]"
                                                    field="rodu"
                                                    placeholder="Tee kuni 3 valikut"
                                                    tooltip-position="left"
                                                    size="small"
                                                    class="w-full max-w-96"
                                                    :maxSelectedLabels="3"
                                                    :selectionLimit="3"
                                                    @update:model-value="updateBuildingPart(part.id, field.key, $event)"
                                                />
                                              </div>
                                            </span>
                                            <span v-else-if="field.key === 'notes'"></span>
                                            <span v-else>{{ field.value }}</span>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <RatingMultiSelect
                    v-else-if="slotProps.data.key === 'ehitise_seisukord'"
                    v-model="building[slotProps.data.key]"
                    field="ehitise_seisukord"
                    :options="[1, 2, 3, 4, 5]"
                    placeholder="Vali hinnang"
                    :maxSelectedLabels="2"
                    :selectionLimit="2"
                    size="small"
                    class="w-full max-w-96"
                    @update:model-value="updateBuildingField(slotProps.data.key, $event)"
                    tooltip-position="left"
                />
                <RatingMultiSelect
                    v-else-if="parkingField === slotProps.data.key"
                    v-model="building[slotProps.data.key]"
                    :options="[1, 2, 3, 4, 5, 6, 7, 8, 9]"
                    field="kinnistu_parkimine"
                    placeholder="Tee kuni 3 valikut"
                    :maxSelectedLabels="3"
                    :selectionLimit="3"
                    size="small"
                    class="w-full max-w-[20.3rem]"
                    @update:model-value="updateBuildingField(slotProps.data.key, $event)"
                    tooltip-position="left"
                />
                <RatingMultiSelect
                  v-else-if="slotProps.data.key === 'panipaik'"
                  v-model="building[slotProps.data.key]"
                  :options="[0, 1, 2]"
                  field="panipaik"
                  placeholder="Tee kuni 2 valikut"
                  tooltip-position="left"
                  size="small"
                  class="w-full max-w-96"
                  :maxSelectedLabels="2"
                  :selectionLimit="2"
                  @update:model-value="updateBuildingField(slotProps.data.key, $event)"
                />
                <InputText
                    v-else-if="inputFields.includes(slotProps.data.key)"
                    v-model="building[slotProps.data.key]"
                    fluid
                    size="small"
                    @blur="updateBuilding()"
                />
                <span v-else>{{ slotProps.data.value }}</span>
            </template>
        </Column>
    </DataTable>
</template>

<script setup>
import { computed, onMounted, ref, toRefs } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTransactionStore } from '@/stores/transaction'
import {
  DETAIL_EXTERNAL_LINKS,
  TRANSACTION_EHR_BUILDING_FIELDS,
  TRANSACTION_EHR_BUILDING_PART_FIELDS,
  TRANSACTION_EHR_FIELDS,
  TRANSACTION_EHR_INPUTFIELDS,
} from '../../../constants'
import { formatLink } from '../../../services/utilities'
import RatingMultiSelect from '@/components/RatingMultiSelect.vue'

const store = useTransactionStore()

const { t, te } = useI18n()

const props = defineProps({
  building: Object,
  title: String,
})

const { building } = toRefs(props)

const inputFields = TRANSACTION_EHR_INPUTFIELDS
const ratingFields = ['ehitise_seisukord']
const parkingField = 'kinnistu_parkimine'
const formValues = ref({})

const expandedParts = ref([])

onMounted(() => {
  formValues.value = {}

  inputFields.forEach(field => {
    formValues.value[field] = building.value[field]
  })

  if (buildingParts.value.length === 1) {
    expandedParts.value = [buildingParts.value[0].id]
  }
})

const togglePart = part => {
  if (expandedParts.value.includes(part)) {
    expandedParts.value = expandedParts.value.filter(p => p !== part)
  } else {
    expandedParts.value.push(part)
  }
}

const computedRows = computed(() => {
  // RED-235: the "Suletud netopind" row is shown ONLY for an üksikelamu
  // (building.is_house_for_area). For every other otstarve — korter,
  // korterelamu, ridaelamu, äri, etc. — the row is omitted entirely, exactly
  // as it was before RED-235 (no empty "Suletud netopind" placeholder).
  const isHouse = !!building.value?.is_house_for_area

  return TRANSACTION_EHR_BUILDING_FIELDS.filter(
    field => field !== 'ehitise_suletud_netopind' || isHouse,
  ).map(field => formatField(field, building.value))
})

const buildingParts = computed(() => {
  return Object.keys(store.selectedTransaction?.building_parts)
    .filter(part => store.selectedTransaction?.building_parts[part].ehr_building_id == Number(props.building.id))
    .map(
        (part, index) => {
            return {
                index: index,
                id: store.selectedTransaction?.building_parts[part].id,
                key: store.selectedTransaction?.building_parts[part].hooneosa_nimetus + ' ' + (store.selectedTransaction?.objekti_liik === 'KOM' ? (store.selectedTransaction?.building_parts[part].hooneosa_number || '') : ''),
                number: store.selectedTransaction?.building_parts[part].hooneosa_number,
                area: store.selectedTransaction?.building_parts[part].hooneosa_pind,
                label: store.selectedTransaction?.building_parts[part].hooneosa_aadress,
                balcony: store.selectedTransaction?.building_parts[part].hooneosa_valipindade_pind || false,
                value: TRANSACTION_EHR_BUILDING_PART_FIELDS.map(field => {
                    return formatField(
                        field,
                        store.selectedTransaction?.building_parts[part],
                    )
                }),
            }
        },
    )
})

const formatField = (field, data) => {
  let value
  switch (field) {
    case 'korrused':
      if (data.hooneosa_sissepaasu_korrus) {
        value = data.hooneosa_sissepaasu_korrus
      } else {
        value = data.ehitise_max_korruste_arv
      }
      break
    case 'ehitise_suletud_netopind':
      // RED-235: building closed net area, with a m² suffix to match the mockup.
      // Blank (not "null m²") when EHR has no value.
      value = data.ehitise_suletud_netopind
        ? `${data.ehitise_suletud_netopind} m²`
        : ''
      break
    default:
      value = data[field]
  }

  return {
    key: field,
    label: t(`messages.transactions.ehr_fields.${field}`),
    value: value,
  }
}

const createEhrLink = row => {
  return formatLink(DETAIL_EXTERNAL_LINKS.externalEHR, { ehrId: row.value })
}

const updateBuilding = () => {
  const dirtyFields = inputFields.filter(
    field => building.value[field] !== formValues.value[field],
  )

  if (dirtyFields.length) {
    store.updateTransactionBuilding({transaction_id: store.selectedTransaction?.id, ...building.value})
  }
}

const updateBuildingField = (field, value) => {
  store.updateTransactionBuildingField({
    id: building.value.id,
    field: field,
    value: value,
    transaction_id: store.selectedTransaction?.id,
  })
}

const updateBuildingPart = (partId, field, value) => {
  store.updateTransactionBuildingPart({
    id: partId,
    field: field,
    value: value,
    transaction_id: store.selectedTransaction?.id,
  })
}
</script>
